import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { DOCUMENT } from '@angular/common';
import {filter, first, map, take} from 'rxjs/operators';
import {Observable, ReplaySubject} from 'rxjs';
import {
  CityDobryAgent,
  DictionaryDobryAgent, DobryAgentBusinessCardNotificationSettings,
  DobryAgentInsuranceCompany, DobryAgentNotificationRecipient,
  FacilityDobryAgent, GooglePlaceDobryAgent,
  PaymentMethodDobryAgent, SubCategory
} from '../interface/facility-dobry-agent';
import { ListResponse } from '../interface/response/list-response';
import {AuthService} from "./auth.service";
import {UserApplicationPermission} from "../interface/user-permissions";

export interface FacilityDobryAgentListParams {
  context?: string;
  onlyUnlinked?: boolean;
  page?: number;
  pageSize?: number;
  disableCount?: boolean;
}

export interface CityDobryAgentSearchParams {
  query?: string;
  page?: number;
  pageSize?: number;
  disableCount?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class FacilityDobryAgentService {
  private readonly facilitiesListSubject: ReplaySubject<ListResponse<FacilityDobryAgent>>
    = new ReplaySubject<ListResponse<FacilityDobryAgent>>(1);
  private readonly insuranceCompanyDobryAgent: ReplaySubject<Array<DobryAgentInsuranceCompany>> = new ReplaySubject();
  private readonly dictionaryDobryAgent: ReplaySubject<Array<DictionaryDobryAgent>> = new ReplaySubject();
  private readonly cityDobryAgent: ReplaySubject<Array<CityDobryAgent>> = new ReplaySubject();
  private readonly paymentMethodDobryAgent: ReplaySubject<Array<PaymentMethodDobryAgent>> = new ReplaySubject(1);
  private readonly subCategoryDobryAgent: ReplaySubject<Array<SubCategory>> = new ReplaySubject(1);

  private initialized = false;

  constructor(
    private httpClient: HttpClient,
    @Inject(DOCUMENT) private document: Document,
    private auth: AuthService,
  ) {
    this.auth.getUserApplicationPermission()
      .pipe(
        filter((permission: UserApplicationPermission) => !!permission?.dobryagent?.active),
        take(1),
      )
      .subscribe(() => {
        this.initDictionaries();
      });
  }

  initDictionaries(): void {
    if (this.initialized) {
      return;
    }

    this.initialized = true;

    this.loadInsuranceCompanyDobryAgent();
    this.loadDictionaryDobryAgent();
    this.loadPaymentMethodDobryAgent();
    this.loadSubCategoryDobryAgent();
  }
  public loadList(params?: FacilityDobryAgentListParams): void {
    this.getList(params)
      .pipe(take(1))
      .subscribe((result: ListResponse<FacilityDobryAgent>) => {
        this.facilitiesListSubject.next(result);
      });
  }

  public getList(params?: FacilityDobryAgentListParams) {
    let httpParams = new HttpParams()
      .set('disableCount', params?.disableCount === false ? '0' : '1');

    if (params?.context) {
      httpParams = httpParams.set('context', params.context);
    }

    if (params?.onlyUnlinked !== undefined) {
      httpParams = httpParams.set('onlyUnlinked', params.onlyUnlinked ? '1' : '0');
    }

    if (params?.page !== undefined) {
      httpParams = httpParams.set('page', String(params.page));
    }

    if (params?.pageSize !== undefined) {
      httpParams = httpParams.set('pageSize', String(params.pageSize));
    }

    return this.httpClient.get<ListResponse<FacilityDobryAgent>>(
      `/rest/dobryagent/api/internal/facilities`,
      { params: httpParams }
    );
  }

  public getFacilityDobryAgentListSubject(): ReplaySubject<ListResponse<FacilityDobryAgent>> {
    return this.facilitiesListSubject;
  }

  public get(addressId: string): Observable<any> {
    return this.httpClient.get(`/rest/dobryagent/api/internal/facility/${addressId}`)
      .pipe(take(1));
  }
  public linkFacilityDobryAgent(formData: { facilityUuid: string, asf24AddressUuid: string }): Observable<any> {
    return this.httpClient.post(`/rest/dobryagent/api/internal/facility/link`, formData)
      .pipe(take(1));
  }

  public sendForm(formData: object, asf24AddressUuid?: string): Observable<any> {
    if (!asf24AddressUuid) {
      return this.httpClient.post(`/rest/dobryagent/api/internal/facility`, formData)
        .pipe(take(1));
    } else {
      return this.httpClient.put(`/rest/dobryagent/api/internal/facility/${asf24AddressUuid}`, formData)
        .pipe(take(1));
    }
  }

  loadInsuranceCompanyDobryAgent() {
    this.httpClient.get('/rest/dobryagent/api/internal/insurance-company')
      .pipe(first())
      .subscribe((response: ListResponse<DobryAgentInsuranceCompany>) => {
        this.insuranceCompanyDobryAgent.next(response.items);
      });
  }

  public getInsuranceCompanyDobryAgentSubject(): ReplaySubject<Array<DobryAgentInsuranceCompany>> {
    return this.insuranceCompanyDobryAgent;
  }

  loadDictionaryDobryAgent() {
    this.httpClient.get('/rest/dobryagent/api/internal/dictionary')
      .pipe(first())
      .subscribe((response: ListResponse<DictionaryDobryAgent>) => {
        this.dictionaryDobryAgent.next(response.items);
      });
  }

  public getDictionaryDobryAgentSubject(): ReplaySubject<Array<DictionaryDobryAgent>> {
    return this.dictionaryDobryAgent;
  }

  loadCityDobryAgent() {
    this.httpClient.get('/rest/dobryagent/api/internal/city')
      .pipe(first())
      .subscribe((response: ListResponse<CityDobryAgent>) => {
        this.cityDobryAgent.next(response.items);
      });
  }

  public getCityDobryAgentSubject(): ReplaySubject<Array<CityDobryAgent>> {
    return this.cityDobryAgent;
  }

  public searchCityDobryAgent(
    query: string,
    pageSize: number = 20,
    page: number = 1
  ): Observable<ListResponse<CityDobryAgent>> {
    let params = new HttpParams()
      .set('page', String(page))
      .set('pageSize', String(pageSize));

    if (query) {
      params = params.set('filter[name]', query);
    }

    return this.httpClient.get<ListResponse<CityDobryAgent>>(
      '/rest/dobryagent/api/internal/city',
      { params }
    ).pipe(take(1));
  }

  public getCityDobryAgentByUuid(cityUuid: string): Observable<CityDobryAgent | null> {
    return this.httpClient.get<CityDobryAgent>(
      `/rest/dobryagent/api/internal/city/${cityUuid}`
    ).pipe(
      map((response: any) => {
        return response?.data ?? response ?? null;
      }),
      take(1),
    );
  }
  loadPaymentMethodDobryAgent(): void {
    this.httpClient.get('/rest/dobryagent/api/internal/payment-method')
      .pipe(first())
      .subscribe((response: ListResponse<PaymentMethodDobryAgent>) => {
        this.paymentMethodDobryAgent.next(response.items);
      });
  }

  public getPaymentMethodDobryAgentSubject(): ReplaySubject<Array<PaymentMethodDobryAgent>> {
    return this.paymentMethodDobryAgent;
  }
  loadSubCategoryDobryAgent(): void {
    const params = new HttpParams()
      .set('allRecords', '1');

    this.httpClient.get('/rest/dobryagent/api/internal/sub-category', { params })
      .pipe(first())
      .subscribe((response: ListResponse<SubCategory>) => {
        this.subCategoryDobryAgent.next(response.items);
      });
  }

  public getSubCategoryDobryAgentSubject(): ReplaySubject<Array<SubCategory>> {
    return this.subCategoryDobryAgent;
  }

  public searchGooglePlace(
    query: string,
    limit: number = 10,
    country: string = 'pl',
    language: string = 'pl',
  ): Observable<ListResponse<GooglePlaceDobryAgent>> {
    const params = new HttpParams()
      .set('q', query)
      .set('limit', String(limit))
      .set('country', country)
      .set('language', language)
      .set('types', 'establishment');

    return this.httpClient.get<ListResponse<GooglePlaceDobryAgent>>(
      '/rest/dobryagent/api/internal/google-place',
      { params }
    ).pipe(take(1));
  }

  uploadAgentImage(agentId: string, file: File): Observable<any> {
    const formData = new FormData();

    formData.append('content', file);

    return this.httpClient.post(
      `/rest/dobryagent/api/internal/agent/${agentId}/image-upload`,
      formData
    ).pipe(take(1));
  }

  public acceptVerification(verificationUuid: string): Observable<any> {
    return this.httpClient.post(
      `/rest/dobryagent/api/internal/verification-facility/verification/${verificationUuid}/accept`,
      {}
    ).pipe(take(1));
  }

  public rejectVerification(verificationUuid: string, rejectionReason: string): Observable<any> {
    return this.httpClient.post(
      `/rest/dobryagent/api/internal/verification-facility/verification/${verificationUuid}/reject`,
      {
        rejectionReason,
      }
    ).pipe(take(1));
  }

  public editAndAcceptVerification(
    verificationUuid: string,
    payload: any
  ): Observable<any> {
    return this.httpClient.put(
      `/rest/dobryagent/api/internal/verification-facility/verification/${verificationUuid}/accept-with-changes`,
      payload
    );
  }

  public getBusinessCardNotificationRecipients(): Observable<DobryAgentBusinessCardNotificationSettings> {
    return this.httpClient.get<DobryAgentBusinessCardNotificationSettings>(
      '/rest/dobryagent/api/internal/application-setting/business-card-change-notification-recipients'
    );
  }

  public updateBusinessCardNotificationRecipients(payload: {
    recipients: DobryAgentNotificationRecipient[];
  }): Observable<{
    success: boolean;
    recipients: DobryAgentNotificationRecipient[];
  }> {
    return this.httpClient.put<{
      success: boolean;
      recipients: DobryAgentNotificationRecipient[];
    }>(
      '/rest/dobryagent/api/internal/application-setting/business-card-change-notification-recipients',
      payload
    );
  }
}
